import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaFacebook, FaYoutube } from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [typingText, setTypingText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isInView, setIsInView] = useState(false);
  
  const fullMessage = "Thank you for looking at my Portfolio";

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    if (!isInView) {
      setTypingText('');
      setShowCursor(true);
      return;
    }

    let timeout;
    if (typingText.length < fullMessage.length) {
      timeout = setTimeout(() => {
        setTypingText(fullMessage.substring(0, typingText.length + 1));
      }, 100);
    } else {
      // Blink cursor after typing is complete
      const cursorInterval = setInterval(() => {
        setShowCursor(prev => !prev);
      }, 530);
      return () => clearInterval(cursorInterval);
    }
    return () => clearTimeout(timeout);
  }, [typingText, fullMessage, isInView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Backend API endpoint - use proxy in development
    const API_URL = process.env.REACT_APP_API_URL || '/api/contact';
    
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Server returned non-JSON response:', text);
        throw new Error('Server error: Please make sure the backend server is running on port 5000');
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Reset status after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      // Check if it's a network error (server not running)
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.warn('⚠️ Server might not be running. Start it with: npm run server');
      }
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: 'Email',
      info: 'ransanilakshayar2004@gmail.com',
      link: 'mailto:ransanilakshayar2004@gmail.com'
    },
    {
      icon: <FaPhone />,
      title: 'Phone',
      info: '+94769656894',
      link: 'tel:+94769656894'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Location',
      info: 'Nugegoda, Sri Lanka',
      link: '#'
    }
  ];

  const socialLinks = [
    { icon: <FaGithub />, url: 'https://github.com', label: 'GitHub' },
    { icon: <FaLinkedin />, url: 'https://www.linkedin.com/in/ransa-n-abeywickrama-a18b3a378?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app', label: 'LinkedIn' },
    { icon: <FaFacebook />, url: 'https://www.facebook.com/ransa.n.abeywickrma', label: 'Facebook' },
    { icon: <FaYoutube />, url: 'https://www.youtube.com/@SPEX_100', label: 'YouTube' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <motion.div
          className="contact-header"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Get In Touch
          </motion.h2>
          <motion.p className="section-subtitle" variants={itemVariants}>
            Let's work together on your next project
          </motion.p>
        </motion.div>

        <div className="contact-content">
          <div className="contact-content-wrapper">
            <motion.div
              className="contact-info"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div className="info-card" variants={itemVariants}>
                <h3>Let's Connect</h3>
                <p>
                  I'm always excited to hear about new opportunities and interesting projects. 
                  Whether you have a question, want to collaborate, need a freelance developer 
                  for your project, or just want to say hello, feel free to reach out! I'm 
                  available for hire and ready to help bring your ideas to life.
                </p>
              </motion.div>

              <div className="contact-details">
                {contactInfo.map((contact, index) => (
                  <motion.a
                    key={index}
                    href={contact.link}
                    className="contact-item"
                    variants={cardVariants}
                    whileHover="hover"
                  >
                    <div className="contact-icon">
                      {contact.icon}
                    </div>
                    <div className="contact-text">
                      <h4>{contact.title}</h4>
                      <p>{contact.info}</p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="contact-form"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
            <motion.form onSubmit={handleSubmit} variants={itemVariants}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>

              <motion.button
                type="submit"
                className="btn btn-primary submit-btn"
                disabled={isSubmitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </motion.button>

              {submitStatus === 'success' && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <p>✅ Message sent successfully! I'll get back to you soon.</p>
                </motion.div>
              )}
              
              {submitStatus === 'error' && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <p>❌ Failed to send message.</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.9 }}>
                    Make sure the backend server is running: <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>npm run server</code>
                  </p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Or contact me directly at: ransanilakshayar2004@gmail.com
                  </p>
                </motion.div>
              )}
            </motion.form>
          </motion.div>
          </div>

          <motion.div 
            className="social-links" 
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h4>Follow Me</h4>
            <div className="social-icons">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="thank-you-message"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            onViewportEnter={() => setIsInView(true)}
            onViewportLeave={() => setIsInView(false)}
          >
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {typingText.length <= 29 ? (
                <span className="thank-you-text-white">{typingText}</span>
              ) : (
                <>
                  <span className="thank-you-text-white">{typingText.substring(0, 29)}</span>
                  <span className="thank-you-text-gold">{typingText.substring(29)}</span>
                </>
              )}
              {showCursor && isInView && <span className="typing-cursor">|</span>}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

